import MeetingMessageButtonEntrySource from '../store/schema/MeetingMessageButtonEntrySource';
import { observer } from 'mobx-react';
import { logUsage } from 'owa-analytics-actions';
import { assertNever } from 'owa-assert';
import loc from 'owa-localize';
import { removeTooltip } from 'owa-locstrings/lib/strings/removetooltip.locstring.json';
import { rsvpLabel } from 'owa-locstrings/lib/strings/rsvplabel.locstring.json';
import {
    isMeetingCancellation,
    isMeetingRequest,
    lazyRemoveCancelledMeetingFromCalendar,
    lazyLogMeetingMessageActionForInvite,
} from 'owa-meeting-message';
import { lazyShowRSVPPeek, lazyHideRSVPPeek, RSVPPeek } from 'owa-rsvp-peek';
import shouldShowRSVPPeek from 'owa-rsvp-peek/lib/selectors/shouldShowRSVPPeek';
import MeetingCancellationMessageType from 'owa-service/lib/contract/MeetingCancellationMessageType';
import MeetingRequestMessageType from 'owa-service/lib/contract/MeetingRequestMessageType';
import { getUserConfiguration } from 'owa-session-store';
import { DefaultButton, IButton, IButtonStyles } from 'office-ui-fabric-react/lib/Button';
import { keydownHandler } from 'owa-hotkeys';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { touchHandler, ITouchHandlerParams } from 'owa-mail-touch-handler';

const styles = require('./MeetingMessageButton.scss');

export interface MeetingMessageButtonProps {
    item: Partial<MeetingRequestMessageType> | MeetingCancellationMessageType;
    entrySource: MeetingMessageButtonEntrySource;
    onAction?: () => void;
    onRemoveMeeting?: () => void;
}

@observer
export default class MeetingMessageButton extends React.Component<MeetingMessageButtonProps, {}> {
    private rsvpButtonRef: HTMLDivElement;

    get touchHandler() {
        const touchHandlerParams: ITouchHandlerParams = {
            onLongPress: this.onContextMenu,
            onClick: this.onRSVP,
        };
        return touchHandler(touchHandlerParams);
    }

    componentDidMount() {
        const { item, entrySource } = this.props;
        let buttonType;

        if (isMeetingRequest(item.ItemClass)) {
            buttonType = 'RSVP';
        } else if (isMeetingCancellation(item.ItemClass)) {
            buttonType = 'Remove';
        } else {
            assertNever(item.ItemClass as never);
        }

        const readingPanePosition = getUserConfiguration().UserOptions
            .GlobalReadingPanePositionReact;
        logUsage('MeetingMessageButtonShown', { buttonType, entrySource, readingPanePosition });
    }

    render() {
        const { item, entrySource } = this.props;

        let actionButton = null;

        const buttonStyles: IButtonStyles = {
            root: styles.container,
        };

        if (isMeetingRequest(item.ItemClass)) {
            actionButton = (
                <div {...this.touchHandler}>
                    <DefaultButton
                        componentRef={this.rsvpButtonRefCallback}
                        styles={buttonStyles}
                        title={loc(rsvpLabel)}
                        text={loc(rsvpLabel)}
                        ariaLabel={loc(rsvpLabel)}
                        onClick={this.onRSVP}
                        onDoubleClick={this.onDoubleClick}
                        onContextMenu={this.onContextMenu}
                    />
                    {shouldShowRSVPPeek(item.ItemId.Id, entrySource) ? (
                        <RSVPPeek
                            item={item}
                            calloutTarget={this.rsvpButtonRef}
                            onAction={this.props.onAction}
                        />
                    ) : null}
                </div>
            );
        } else if (isMeetingCancellation(item.ItemClass)) {
            actionButton = (
                <div {...touchHandler({ onClick: this.onRemove })}>
                    <DefaultButton
                        styles={buttonStyles}
                        title={loc(removeTooltip)}
                        text={loc(removeTooltip)}
                        ariaLabel={loc(removeTooltip)}
                        onClick={this.onRemove}
                        onDoubleClick={this.onDoubleClick}
                    />
                </div>
            );
        }

        return actionButton;
    }

    private rsvpButtonRefCallback = (ref: IButton) => {
        this.rsvpButtonRef = ReactDOM.findDOMNode(ref as any) as HTMLDivElement;
    };

    private onRemove = (ev?: any) => {
        ev.stopPropagation();
        lazyLogMeetingMessageActionForInvite.importAndExecute(
            'RemoveFromCalendar',
            this.props.item,
            'RSVPPeek'
        );
        lazyRemoveCancelledMeetingFromCalendar.importAndExecute(this.props.item);
        this.props.onAction && this.props.onAction();
        this.props.onRemoveMeeting && this.props.onRemoveMeeting();
    };

    private onRSVP = (evt?: any) => {
        evt.stopPropagation();
        lazyShowRSVPPeek.importAndExecute(this.props.item, this.props.entrySource);
    };

    private onContextMenu(ev: React.MouseEvent<HTMLDivElement>) {
        ev.preventDefault();
        ev.stopPropagation();
    }

    private onDoubleClick(ev: React.MouseEvent<HTMLDivElement>) {
        ev.preventDefault();
        ev.stopPropagation();
    }

    @keydownHandler('enter', true, true)
    onShowPeek(evt: KeyboardEvent) {
        if (isMeetingRequest(this.props.item.ItemClass)) {
            this.onRSVP(evt);
        } else {
            this.onRemove(evt);
        }
    }

    // No prevent default, no stop propagation.
    // Will let it bubble up so LV capture if no action was done
    @keydownHandler('escape', false, false)
    onClose(evt: KeyboardEvent) {
        // Just hide and swallow the events if the peek is being shown
        const { item, entrySource } = this.props;
        if (isMeetingRequest(item.ItemClass) && shouldShowRSVPPeek(item.ItemId.Id, entrySource)) {
            evt.preventDefault();
            evt.stopPropagation();
            lazyHideRSVPPeek.importAndExecute();
        }
    }
}
